import type { TypePolicies } from "@apollo/client";
import { mergeObjects } from "./apollo-client-merge.js";
import { relayStylePagination } from "./apollo-client-pagination.js";
import type { ChangeLogFilters } from "./graphqlTypes.js";

export const typePolicies: TypePolicies = {
  Query: {
    fields: {
      requirement: {
        keyArgs: args =>
          args
            ? Object.keys(args).filter(
                (arg: string) => arg !== "expandedItemIds"
              )
            : undefined,
      },
      quote: {
        keyArgs: args =>
          args
            ? Object.keys(args).filter(
                (arg: string) => arg !== "expandedItemIds"
              )
            : undefined,
      },
      building: {
        keyArgs: args =>
          args
            ? Object.keys(args).filter(
                (arg: string) => arg !== "expandedItemIds"
              )
            : undefined,
      },
      quoteTemplateLatest: {
        keyArgs: args =>
          args
            ? Object.keys(args).filter(
                (arg: string) => arg !== "expandedItemIds"
              )
            : undefined,
      },
      quoteTemplateVersion: {
        keyArgs: args =>
          args
            ? Object.keys(args).filter(
                (arg: string) => arg !== "expandedItemIds"
              )
            : undefined,
      },
      wizardItem: {
        keyArgs: args =>
          args
            ? Object.keys(args).filter(
                (arg: string) => arg !== "applyItemActions"
              )
            : undefined,
      },
      pimSearchProducts: {
        keyArgs: args =>
          args ? Object.keys(args).filter(arg => arg !== "limit") : undefined,
      },
      projects: relayStylePagination((args, context) => {
        return args
          ? Object.keys(args).filter(
              arg =>
                arg !== "limit" &&
                arg !== "offset" &&
                arg !== "before" &&
                arg !== "after" &&
                arg !== "first" &&
                arg !== "last"
            )
          : undefined;
      }),
      quoteChangeLog: relayStylePagination(
        args => {
          // Key the connection on docId / projectId / filters; merge pages that
          // differ only by the cursor window (after / first). The filter arrays are
          // sorted so member order within a dimension does not fork the cache slice
          // (`[a,b]` and `[b,a]` share one), while distinct filter sets stay distinct.
          const filters = (args?.filters ?? {}) as ChangeLogFilters;
          const sorted = (xs?: readonly string[] | null) =>
            xs && xs.length > 0 ? [...xs].sort() : [];
          return JSON.stringify({
            docId: args?.docId ?? null,
            projectId: args?.projectId ?? null,
            entityIds: sorted(filters.entityIds),
            actors: sorted(filters.actors),
            occurredFrom: filters.occurredFrom ?? null,
            occurredTo: filters.occurredTo ?? null,
          });
        },
        {
          // `union-overlap`, not the default replace: the change-log is
          // append-only in keyset order (occurred_at DESC, id DESC) and its
          // cursors (base64 of {occurredAt, id}, immutable fields, selected
          // on every edge) are row-stable, so cursor equality ≡ row
          // identity; the backend also withholds trailing partial groups,
          // so an overlap cut can never split a UI row group (adjacency by
          // correlationId). Trade-off: a server-side hard delete (GDPR /
          // retention) leaves a ghost row in the cached tail until reload —
          // accepted because the log is append-only in normal operation and
          // the cache is session-scoped.
          cursorlessMerge: "union-overlap",
        }
      ),
      projectChangeLog: relayStylePagination(
        args => {
          // Like `quoteChangeLog` but keyed on the project (no docId): merge pages that
          // differ only by the cursor window (after / first), while distinct filter
          // sets stay distinct. Filter arrays are sorted so member order does not fork
          // the cache slice.
          const filters = (args?.filters ?? {}) as ChangeLogFilters;
          const sorted = (xs?: readonly string[] | null) =>
            xs && xs.length > 0 ? [...xs].sort() : [];
          return JSON.stringify({
            projectId: args?.projectId ?? null,
            entityIds: sorted(filters.entityIds),
            actors: sorted(filters.actors),
            occurredFrom: filters.occurredFrom ?? null,
            occurredTo: filters.occurredTo ?? null,
          });
        },
        // Same `union-overlap` reasoning as `quoteChangeLog` above.
        { cursorlessMerge: "union-overlap" }
      ),
      categories: {
        keyArgs: ["ids", "excludeIds"],
      },
    },
  },
  Subscription: {
    fields: {
      // Each emission is an unrelated event of the `AgentStreamEvent` union
      // (ping, tool call, text delta, the final result, ...), most of whose
      // members have no `id` — the default merge would try to combine two
      // unrelated shapes at the same `ROOT_SUBSCRIPTION.agentMessageStream`
      // position and warn about data loss (MEIS-13358: that warning crashes
      // the Usersnap console wrapper). Always replace instead.
      agentMessageStream: {
        merge: false,
      },
    },
  },
  Organisation: {
    fields: {},
  },
  OrganisationDefaults: {
    fields: {
      defaultMaterialMarginRanges: {
        merge(existing, incoming) {
          return mergeObjects(existing, incoming);
        },
      },
    },
  },
  OrganisationProfile: {
    fields: {
      shoppableTemplate: {
        keyArgs: args =>
          args
            ? Object.keys(args).filter(
                (arg: string) =>
                  arg !== "expandedItemIds" &&
                  arg !== "templateVariantConfiguration"
              )
            : undefined,
      },
      _3d_roomShoppableTemplate: {
        keyArgs: ["docId"],
      },
    },
  },
  Project: {
    fields: {
      billOfMaterialsItems: {
        keyArgs: args => args?.id || args?.quoteItemStatus || undefined,
      },
    },
  },
  QuoteTemplate: {
    keyFields: (object, context) => {
      if (object.resolvedAsReadModelVersionNumber === undefined) {
        const error = new Error(
          `Must include 'resolvedAsReadModelVersionNumber' in QuoteTemplate`
        );

        throw error;
      }

      if (object.resolvedAsReadModelVersionNumber === null) {
        return `${object.__typename}:${object.id}`;
      }

      return `${object.__typename}:${object.id}:Version:${object.resolvedAsReadModelVersionNumber}`;
    },
    fields: {
      items: {
        keyArgs: (args, context) => {
          return args ? Object.keys(args) : context.field?.alias?.value;
        },
        merge: (existing, incoming) => {
          return incoming;
        },
      },
    },
  },
  ShoppableTemplate: {
    fields: {
      items: {
        keyArgs: false,
        merge: (existing, incoming) => {
          return incoming;
        },
      },
    },
  },
  Requirement: {
    fields: {
      items: {
        merge: (existing, incoming) => {
          return incoming;
        },
      },
    },
  },
  Quote: {
    fields: {
      items: {
        merge: (existing, incoming) => {
          return incoming;
        },
      },
    },
  },
  TaskDocument: {
    fields: {
      items: {
        merge: (existing, incoming) => {
          return incoming;
        },
      },
    },
  },
  Building: {
    fields: {
      items: {
        merge: (existing, incoming) => {
          return incoming;
        },
      },
      buildingAddress: {
        merge(existing, incoming) {
          if (existing?.id === incoming.id) {
            return mergeObjects(existing, incoming);
          } else {
            return incoming;
          }
        },
      },
    },
  },
  Item: {
    keyFields: (object, context) => {
      if (object.originVersionNumber === undefined) {
        const error = new Error(`Must include 'originVersionNumber' in Item`);

        throw error;
      }

      if (object.originVersionNumber === null) {
        return `${object.__typename}:${object.id}`;
      }

      return `${object.__typename}:${object.id}:Version:${object.originVersionNumber}`;
    },
    fields: {
      templateSearchSortingDefinitions: {
        merge: (existing, incoming) => {
          return incoming;
        },
      },
      templateSearchSortingDefaults: {
        merge: (existing, incoming) => {
          return incoming;
        },
      },
      props2: {
        merge: (existing, incoming) => {
          return incoming;
        },
      },
      props2WithoutValue: {
        merge: (existing, incoming) => {
          return incoming;
        },
      },
    },
  },
  BillOfMaterialsItem: {
    keyFields: ["id", "quantityRequired", "quantityRequiredTotal"],
  },
  InvoiceItem: {
    fields: {
      itemSnapshot: {
        merge(existing, incoming) {
          return mergeObjects(existing, incoming);
        },
      },
    },
  },
  ItemSnapshot: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  EntityFieldMetadata: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  QuoteSapS4HanaCustomField: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  // MEIS-13357: `Query.sapValueHelp` is a union field (`SapS4HanaValueHelp__Response`),
  // and three queries write its `SapS4HanaValueHelp` data member —
  // `CrmCreateModal__SapData` (businessPartnerSalesOffices, salutations,
  // companyCodes, cashPlanningGroup), `CrmCreateModal__SapSalesPersons`
  // (salesPersons), and `CrmCreateModal__SapPaymentTerms` (paymentTerms),
  // the latter two fetched lazily. The type has no natural key, so without
  // this policy Apollo replaces the whole object on the second write
  // instead of merging the partial writes.
  // The policy lives on the type (not on the `sapValueHelp` field) so it
  // only applies when the incoming `__typename` is `SapS4HanaValueHelp` —
  // a field-level policy would also fold the union's error members
  // (`MissingPermissions` / `MissingCapabilities`) into data writes.
  SapS4HanaValueHelp: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  PimProductMetaInfo: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  ItemCalculation: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  ItemWizardSettings: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  ProposedItemCalculations: {
    fields: {
      selected: {
        merge(existing, incoming) {
          return mergeObjects(existing, incoming);
        },
      },
    },
  },
  ItemProduct: {
    fields: {
      // The product's properties are replaced wholesale; their shape differs
      // per product, so Apollo's data-loss warning does not apply.
      props2: { merge: false },
    },
  },
  ItemProductSearchFilterDefinitions: {
    keyFields: ["itemId"],
    fields: {
      propertyFilters: {
        merge(existing, incoming) {
          return mergeObjects(existing, incoming);
        },
      },
    },
  },
  DefaultProductSearchFilters: {
    keyFields: ["itemId"],
    fields: {
      propertyFilters: {
        merge(existing, incoming) {
          return mergeObjects(existing, incoming);
        },
      },
    },
  },
  Address: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  AddressSnapshot: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  DocActorContact: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  TemplateTypeAtRevision: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  Channel: {
    keyFields: ["id"],
    fields: {
      messages: relayStylePagination((args, context) => {
        return args
          ? Object.keys(args).filter(
              arg =>
                arg !== "limit" &&
                arg !== "offset" &&
                arg !== "before" &&
                arg !== "after" &&
                arg !== "first" &&
                arg !== "last"
            )
          : undefined;
      }),
    },
  },
  Meister1Flow: {
    keyFields: ["id", "label", "identName"],
  },
  RecommendedTemplate: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  RecommendedTemplateType: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  ProjectRequirementsConnection: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  ProjectIncomingInvoiceConnection: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  ProjectOutgoingInvoiceConnection: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  ProjectIncomingQuoteConnection: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  ProjectOutgoingQuoteConnection: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  RequirementsConnection: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  IncomingInvoiceConnection: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  OutgoingInvoiceConnection: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  IncomingQuoteConnection: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  OutgoingQuoteConnection: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  TodoItemsConnection: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  OrderConnection: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  TemplateSubscription: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
  PimSupplierProductStatus: {
    keyFields: false,
    merge(existing, incoming) {
      return mergeObjects(existing, incoming);
    },
  },
};
